import { generateAssets } from "./testData"; // Adjust the import path as needed

export interface ClassificationTreeItem {
  id: number;
  name: string;
  level: number;
  parentId?: number;
  assetIds?: (string | number)[];
}

export interface ClassificationTree {
  id: number;
  name: string;
  items?: ClassificationTreeItem[];
}

// Hardcoded sector trees that map to actual asset sectors and subsectors
export const generateClassificationTrees = (): ClassificationTree[] => {
  // Import or define your assets list here

  // Map from asset country codes to classification tree country names
  const countryCodeToName: { [code: string]: string } = {
    USA: "United States",
    GBR: "United Kingdom",
    CHE: "Switzerland",
    FRA: "France",
    NLD: "Netherlands",
    DNK: "Denmark",
    DEU: "Germany",
    EUR: "Eurozone",
    AUS: "Australia",
  };

  // Group asset IDs by country
  const countryAssetMap: { [country: string]: (string | number)[] } = {};
  generateAssets().forEach(
    (asset: { id: string | number; country?: string }) => {
      const countryCode = asset.country || "UNKNOWN";
      const countryName = countryCodeToName[countryCode] || "Unclassified";

      if (!countryAssetMap[countryName]) {
        countryAssetMap[countryName] = [];
      }

      countryAssetMap[countryName].push(asset.id);
    }
  );

  // Helper to get asset IDs for a country or empty array
  const getAssetIdsByCountry = (country: string) =>
    countryAssetMap[country] || [];

  return [
    {
      id: 1,
      name: "Geographical",
      items: [
        {
          id: 101,
          name: "Americas",
          level: 1,
        },
        {
          id: 102,
          name: "Europe",
          level: 1,
        },
        {
          id: 103,
          name: "Asia",
          level: 1,
        },
        {
          id: 104,
          name: "Oceania",
          level: 1,
        },
        {
          id: 105,
          name: "Unclassified",
          level: 1,
        },
        {
          id: 1001,
          name: "United States",
          level: 2,
          parentId: 101,
          assetIds: getAssetIdsByCountry("United States"),
        },
        {
          id: 1003,
          name: "United Kingdom",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("United Kingdom"),
        },
        {
          id: 1004,
          name: "Switzerland",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("Switzerland"),
        },
        {
          id: 1005,
          name: "European",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("European"),
        },
        {
          id: 1006,
          name: "Denmark",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("Denmark"),
        },
        {
          id: 1007,
          name: "Netherlands",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("Netherlands"),
        },
        {
          id: 1008,
          name: "France",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("France"),
        },
        {
          id: 1009,
          name: "Germany",
          level: 2,
          parentId: 102,
          assetIds: getAssetIdsByCountry("Germany"),
        },
        {
          id: 1010,
          name: "Australia",
          level: 2,
          parentId: 104,
          assetIds: getAssetIdsByCountry("Australia"),
        },
        {
          id: 1011,
          name: "Unclassified",
          level: 2,
          parentId: 105,
          assetIds: [],
        },
      ],
    },
    {
      id: 2,
      name: "Custom Classification",
      items: [
        {
          id: 200,
          name: "Unclassified",
          level: 1,
          parentId: 2,
          assetIds: [],
        },
        {
          id: 20001,
          name: "Unclassified",
          level: 2,
          parentId: 200,
          assetIds: [],
        },
        {
          id: 2000101,
          name: "Unclassified",
          level: 3,
          parentId: 20001,
          assetIds: [1, 2, 4, 7, 10, 12, 13, 16, 17, 18, 19, 20, 22, 23, 24, 25, 26, 27, 30, 31, 
            32, 34, 35, 37, 38, 41, 42, 43, 45, 46, 47, 48, 51, 
            52, 59, 60, 61, 62, 63, 64, 65, 66, 67, 69, 70, 71, 72, 73],
        },
        // Treasury
        {
          id: 201,
          name: "Treasury",
          level: 1,
          parentId: 2,
          assetIds: [],
        },
        {
          id: 20101,
          name: "Australian Cash",
          level: 2,
          parentId: 201,
          assetIds: [],
        },
        {
          id: 20102,
          name: "Australian Fixed Interest",
          level: 2,
          parentId: 201,
          assetIds: [],
        },
        {
          id: 20103,
          name: "International Fixed Interest",
          level: 2,
          parentId: 201,
          assetIds: [],
        },
        {
          id: 2010101,
          name: "Cash",
          level: 3,
          parentId: 20101,
          assetIds: [40],
        },
        {
          id: 2010102,
          name: "Cash Equivalents",
          level: 3,
          parentId: 20101,
          assetIds: [],
        },
        {
          id: 2010201,
          name: "Exchange Traded Bonds",
          level: 3,
          parentId: 20102,
          assetIds: [],
        },
        {
          id: 2010202,
          name: "Government Bonds",
          level: 3,
          parentId: 20102,
          assetIds: [6],
        },
        {
          id: 2010301,
          name: "Exchange Traded Bonds",
          level: 3,
          parentId: 20103,
          assetIds: [],
        },
        {
          id: 2010302,
          name: "Government Bonds",
          level: 3,
          parentId: 20103,
          assetIds: [9, 21],
        },
        // Equities
        {
          id: 202,
          name: "Equities",
          level: 1,
          parentId: 2,
          assetIds: [],
        },
        {
          id: 20201,
          name: "Australian Equities",
          level: 2,
          parentId: 202,
          assetIds: [],
        },
        {
          id: 20202,
          name: "International Equities",
          level: 2,
          parentId: 202,
          assetIds: [],
        },
        {
          id: 2020101,
          name: "Metals and Mining",
          level: 3,
          parentId: 20201,
          assetIds: [],
        },
        {
          id: 2020102,
          name: "Financials",
          level: 3,
          parentId: 20201,
          assetIds: [51],
        },
        {
          id: 2020103,
          name: "Information Technology",
          level: 3,
          parentId: 20201,
          assetIds: [49, 50, 53, 55, 56],
        },
        {
          id: 2020201,
          name: "Energy",
          level: 3,
          parentId: 20202,
          assetIds: [68],
        },
        {
          id: 2020202,
          name: "Industrials",
          level: 3,
          parentId: 20202,
          assetIds: [],
        },
        {
          id: 2020203,
          name: "Materials",
          level: 3,
          parentId: 20202,
          assetIds: [14, 36],
        },
        // Property
        {
          id: 203,
          name: "Property",
          level: 1,
          parentId: 2,
          assetIds: [],
        },
        {
          id: 20301,
          name: "Australian Property",
          level: 2,
          parentId: 203,
          assetIds: [],
        },
        {
          id: 2030101,
          name: "Residential Property",
          level: 3,
          parentId: 20301,
          assetIds: [3,8,15, 17],
        },
        {
          id: 2030102,
          name: "Commercial Property",
          level: 3,
          parentId: 20301,
          assetIds: [28],
        },
        {
          id: 2030103,
          name: "Industrial Property",
          level: 3,
          parentId: 20301,
          assetIds: [29],
        },
        // Alternative Investments
        {
          id: 204,
          name: "Alternative Investments",
          level: 1,
          parentId: 2,
          assetIds: [],
        },
        {
          id: 20401,
          name: "Private Credit Funds",
          level: 2,
          parentId: 204,
          assetIds: [],
        },
        {
          id: 2040101,
          name: "Direct Lending",
          level: 3,
          parentId: 20401,
          assetIds: [],
        },
        {
          id: 2040102,
          name: "Distressed Debt",
          level: 3,
          parentId: 20401,
          assetIds: [33, 39, 44],
        },
      ],
    },
  ];
};

// Helper function to get available levels for a tree
export const getTreeLevels = (treeId: number): number[] => {
  const trees = generateClassificationTrees();
  const tree = trees.find((t) => t.id === treeId);
  if (!tree || !tree.items) return [];

  const levels = new Set<number>();
  tree.items.forEach((item) => levels.add(item.level));
  return Array.from(levels).sort();
};

// Helper function to get items for a specific tree and level
export const getTreeItemsByLevel = (
  treeId: number,
  level: number
): ClassificationTreeItem[] => {
  const trees = generateClassificationTrees();
  const tree = trees.find((t) => t.id === treeId);
  if (!tree || !tree.items) return [];

  return tree.items.filter((item) => item.level === level);
};
